import React, { useState, createContext, useEffect, useContext, useCallback, useMemo } from "react";
import { useTranslation } from "@unicity/design-system";
import { LANGUAGE_DETAILS } from "../common/languageDetails";
import { LanguageContextType } from "../types/components/language";

const LanguageContext = createContext<LanguageContextType>({
  currentLanguage: "en",
  setCurrentLanguage: async () => {},
  languageDir: "ltr",
  setLanguageDir: () => { },
  isLoading: false,
  localizeNumber: () => ""
});

interface LanguageContextProviderProps {
  children: React.ReactNode;
}

const LanguageContextProvider: React.FC<LanguageContextProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  
  // PERSISTENCE LAYER - Force restore saved language on every app load
  const PERSISTENT_LANG_KEY = 'i18nextLng';
  const savedLanguage = localStorage.getItem(PERSISTENT_LANG_KEY);
  // Determine initial language with fallback chain
  const getInitialLanguage = () => {
    if (savedLanguage && LANGUAGE_DETAILS.some(lang => lang.i18nCode === savedLanguage)) {
      return savedLanguage;
    }
    localStorage.setItem(PERSISTENT_LANG_KEY, "en-US");
    return "en-US";
  };
  
  const [currentLanguage, setCurrentLanguage] = useState<string>(getInitialLanguage());
  const [languageDir, setLanguageDir] = useState<"ltr" | "rtl">("ltr");
  const [isLoading, setIsLoading] = useState(false);

  const localizeNumber = useCallback((num: number): string => {
    const formatted = new Intl.NumberFormat(currentLanguage).format(num);
    return formatted;
  }, [currentLanguage]);

  // FORCE PERSISTENCE on mount - override any design-system initialization
  useEffect(() => {
    const forcePersistence = async () => {
      const persistedLang = localStorage.getItem(PERSISTENT_LANG_KEY);
      
      if (persistedLang) {
        try {
          setIsLoading(true);
          await i18n.changeLanguage(persistedLang);
          setCurrentLanguage(persistedLang);
          // Also update i18nextLng for design-system compatibility
          localStorage.setItem('i18nextLng', persistedLang);
        } catch (error) {
          console.error('Failed to restore persisted language:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    // Force persistence immediately and after delay
    forcePersistence();
    const timer = setTimeout(forcePersistence, 100);
    const timer2 = setTimeout(forcePersistence, 500); // Double check
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, [i18n, PERSISTENT_LANG_KEY, currentLanguage]);

  // Additional persistence check on window focus (in case something resets it)
  useEffect(() => {
    const handleFocus = () => {
      const persistedLang = localStorage.getItem(PERSISTENT_LANG_KEY);
      if (persistedLang && persistedLang !== currentLanguage && persistedLang !== i18n.language) {
        i18n.changeLanguage(persistedLang);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [i18n, currentLanguage, PERSISTENT_LANG_KEY]);

  // Listen to i18next changes but maintain our persistence
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      if (lng && lng !== 'cimode') {
        const isSupported = LANGUAGE_DETAILS.some(lang => lang.i18nCode === lng);
        if (isSupported) {
          setCurrentLanguage(lng);
          // Always save to our persistent storage
          localStorage.setItem(PERSISTENT_LANG_KEY, lng);
          localStorage.setItem('i18nextLng', lng);
        }
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n, PERSISTENT_LANG_KEY]);

  // Custom setCurrentLanguage that forces persistence
  const updateCurrentLanguage = async (language: string) => {
    try {
      setIsLoading(true);
      // Immediately save to persistent storage
      localStorage.setItem(PERSISTENT_LANG_KEY, language);
      localStorage.setItem('i18nextLng', language);
      
      await i18n.changeLanguage(language);
      setCurrentLanguage(language);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.dir = languageDir;
  }, [languageDir]);

  useEffect(() => {
    if (currentLanguage === "ar") {
      setLanguageDir("rtl");
    } else {
      setLanguageDir("ltr");
    }
  }, [currentLanguage]);

  const contextValue = useMemo(() => ({
    currentLanguage,
    setCurrentLanguage,
    languageDir,
    setLanguageDir,
    isLoading,
    localizeNumber
  }), [currentLanguage, languageDir, isLoading, localizeNumber]);

  return (
    <LanguageContext.Provider
      value={{
        ...contextValue,
        setCurrentLanguage: updateCurrentLanguage
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguageContext = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageContextProvider');
  }
  return context;
};

export { LanguageContext, LanguageContextProvider, useLanguageContext }; 